// src/constants/activityThresholds.js
// Configurable thresholds for safety risk scoring per outdoor activity.
// Each activity has factors with thresholds for yellow/orange/red risk levels.
// "label" is user-facing, "explain" describes what triggers each level.

const activityThresholds = {
  hiking: {
    label: "Hiking",
    factors: {
      aqi: {
        label: "Air Quality (AQI)",
        yellow: { min: 101, explain: "AQI 101–150: Unhealthy for sensitive groups" },
        orange: { min: 151, explain: "AQI 151–200: Unhealthy for all" },
        red:    { min: 201, explain: "AQI > 200: Very unhealthy / hazardous" },
      },
      wind: {
        label: "Wind Speed",
        unit: "mph",
        yellow: { min: 25, explain: "Wind > 25 mph: Difficult on exposed trails" },
        orange: { min: 40, explain: "Wind > 40 mph: Dangerous on ridgelines" },
        red:    { min: 58, explain: "Wind > 58 mph: Hurricane-force, avoid outdoors" },
      },
      tempHigh: {
        label: "High Temperature",
        unit: "°F",
        yellow: { min: 95, explain: "Temp > 95°F: Heat exhaustion risk" },
        orange: { min: 105, explain: "Temp > 105°F: Heat stroke danger" },
        red:    { min: 115, explain: "Temp > 115°F: Extreme heat emergency" },
      },
      tempLow: {
        label: "Low Temperature",
        unit: "°F",
        yellow: { max: 25, explain: "Temp < 25°F: Hypothermia risk" },
        orange: { max: 10, explain: "Temp < 10°F: Frostbite risk" },
        red:    { max: -10, explain: "Temp < -10°F: Extreme cold danger" },
      },
      lightning: {
        label: "Lightning / Thunderstorm",
        yellow: { keyword: "thunder", explain: "Thunderstorms in forecast" },
        orange: { keyword: "severe thunderstorm", explain: "Severe thunderstorm watch/warning" },
        red:    { keyword: "tornado", explain: "Tornado watch/warning in area" },
      },
      rain: {
        label: "Precipitation",
        yellow: { keyword: "rain", explain: "Rain in forecast: Slippery trails, increased flash flood risk" },
        orange: { keyword: "heavy rain", explain: "Heavy rain: Trail damage, mudslides possible" },
        red:    { keyword: "flash flood", explain: "Flash flood risk — stay away from canyons and water crossings" },
      },
      snow: {
        label: "Snow",
        yellow: { keyword: "snow", explain: "Snow in forecast: Slippery trails, reduced visibility" },
        orange: { keyword: "heavy snow", explain: "Heavy snow: Trail navigation difficult, avalanche risk in mountains" },
        red:    { keyword: "blizzard", explain: "Blizzard conditions: Do not hike, whiteout risk" },
      },
      npsAlert: {
        label: "NPS Park Alert",
        yellow: { category: "caution", explain: "NPS caution alert for area" },
        orange: { category: "closure", explain: "Trail or area closure" },
        red:    { category: "danger", explain: "NPS danger alert active" },
      },
      nwsAlert: {
        label: "NWS Weather Alert",
        yellow: { severity: "Minor", explain: "NWS minor advisory in area" },
        orange: { severity: "Moderate", explain: "NWS moderate alert / watch" },
        red:    { severity: "Severe", explain: "NWS severe warning active" },
      },
    },
  },
  kayaking: {
    label: "Kayaking / Rafting",
    factors: {
      tempHigh: {
        label: "High Temperature",
        unit: "°F",
        yellow: { min: 95, explain: "Temp > 95°F: Increased heat exhaustion risk" },
        orange: { min: 105, explain: "Temp > 105°F: Heat stroke danger" },
        red:    { min: 115, explain: "Temp > 115°F: Extreme heat emergency" },
      },
      tempLow: {
        label: "Low Temperature",
        unit: "°F",
        yellow: { max: 35, explain: "Temp < 35°F: Hypothermia risk from water immersion" },
        orange: { max: 20, explain: "Temp < 20°F: Rapid hypothermia danger" },
        red:    { max: 0, explain: "Temp < 0°F: Extreme cold emergency" },
      },
      riverFlow: {
        label: "River Flow (CFS)",
        yellow: { pctAboveNormal: 20,  explain: "Flow 20%+ above seasonal average" },
        orange: { pctAboveNormal: 50,  explain: "Flow 50%+ above seasonal average" },
        red:    { pctAboveNormal: 100, explain: "Flow 100%+ above seasonal average" },
      },
      gageHeight: {
        label: "Gage Height",
        unit: "ft",
        yellow: { risingFast: true, explain: "Gage height rising rapidly" },
        orange: { keyword: "flood watch", explain: "Flood watch upstream" },
        red:    { keyword: "flood warning", explain: "Flood warning active" },
      },
      rain: {
        label: "Precipitation",
        yellow: { keyword: "rain", explain: "Rain in forecast: Reduced visibility, water murkiness" },
        orange: { keyword: "heavy rain", explain: "Heavy rain: Dangerous currents, flash flood risk" },
        red:    { keyword: "flash flood", explain: "Flash flood warning — do not launch" },
      },
      snow: {
        label: "Snow",
        yellow: { keyword: "snow", explain: "Snow in forecast: Cold water immersion risk increases" },
        orange: { keyword: "heavy snow", explain: "Heavy snow: Dangerous conditions, ice forming" },
        red:    { keyword: "blizzard", explain: "Blizzard conditions: Do not launch" },
      },
      wind: {
        label: "Wind Speed",
        unit: "mph",
        yellow: { min: 20, explain: "Wind > 20 mph: Choppy water" },
        orange: { min: 30, explain: "Wind > 30 mph: Capsizing risk" },
        red:    { min: 45, explain: "Wind > 45 mph: Do not launch" },
      },
      nwsAlert: {
        label: "NWS Weather Alert",
        yellow: { severity: "Minor", explain: "NWS minor advisory in area" },
        orange: { severity: "Moderate", explain: "NWS moderate alert / watch" },
        red:    { severity: "Severe", explain: "NWS severe warning active" },
      },
    },
  },
  camping: {
    label: "Camping",
    factors: {
      tempHigh: {
        label: "High Temperature",
        unit: "°F",
        yellow: { min: 95, explain: "Temp > 95°F: Heat exhaustion risk" },
        orange: { min: 105, explain: "Temp > 105°F: Heat stroke danger" },
        red:    { min: 115, explain: "Temp > 115°F: Extreme heat emergency" },
      },
      wind: {
        label: "Wind Speed",
        unit: "mph",
        yellow: { min: 30, explain: "Wind > 30 mph: Tent setup difficult" },
        orange: { min: 45, explain: "Wind > 45 mph: Structural damage risk" },
        red:    { min: 58, explain: "Wind > 58 mph: Hurricane-force" },
      },
      lightning: {
        label: "Lightning / Thunderstorm",
        yellow: { keyword: "thunder", explain: "Thunderstorms in forecast" },
        orange: { keyword: "severe thunderstorm", explain: "Severe thunderstorm watch/warning" },
        red:    { keyword: "tornado", explain: "Tornado watch/warning" },
      },
      rain: {
        label: "Precipitation",
        yellow: { keyword: "rain", explain: "Rain in forecast: Tent setup difficult, damp conditions" },
        orange: { keyword: "heavy rain", explain: "Heavy rain: Flooding risk, muddy ground" },
        red:    { keyword: "flash flood", explain: "Flash flood warning — evacuate to higher ground" },
      },
      snow: {
        label: "Snow",
        yellow: { keyword: "snow", explain: "Snow in forecast: Cold night, extra insulation needed" },
        orange: { keyword: "heavy snow", explain: "Heavy snow: Tent collapse risk, access roads may close" },
        red:    { keyword: "blizzard", explain: "Blizzard conditions: Do not camp, seek shelter" },
      },
      tempLow: {
        label: "Low Temperature",
        unit: "°F",
        yellow: { max: 25, explain: "Temp < 25°F: Hypothermia risk" },
        orange: { max: 10, explain: "Temp < 10°F: Frostbite risk" },
        red:    { max: -10, explain: "Temp < -10°F: Extreme cold danger" },
      },
      aqi: {
        label: "Air Quality (AQI)",
        yellow: { min: 101, explain: "AQI 101–150: Unhealthy for sensitive groups" },
        orange: { min: 151, explain: "AQI 151–200: Unhealthy for all" },
        red:    { min: 201, explain: "AQI > 200: Hazardous" },
      },
      npsAlert: {
        label: "NPS Park Alert",
        yellow: { category: "caution", explain: "NPS caution alert for area" },
        orange: { category: "closure", explain: "Area closure" },
        red:    { category: "danger", explain: "NPS danger alert active" },
      },
      nwsAlert: {
        label: "NWS Weather Alert",
        yellow: { severity: "Minor", explain: "NWS minor advisory in area" },
        orange: { severity: "Moderate", explain: "NWS moderate alert / watch" },
        red:    { severity: "Severe", explain: "NWS severe warning active" },
      },
    },
  },
  fishing: {
    label: "Fishing",
    factors: {
      tempHigh: {
        label: "High Temperature",
        unit: "°F",
        yellow: { min: 95, explain: "Temp > 95°F: Increased heat exhaustion risk" },
        orange: { min: 105, explain: "Temp > 105°F: Heat stroke danger" },
        red:    { min: 115, explain: "Temp > 115°F: Extreme heat emergency" },
      },
      tempLow: {
        label: "Low Temperature",
        unit: "°F",
        yellow: { max: 30, explain: "Temp < 30°F: Hypothermia risk from water exposure" },
        orange: { max: 15, explain: "Temp < 15°F: Rapid hypothermia danger" },
        red:    { max: 0, explain: "Temp < 0°F: Extreme cold emergency" },
      },
      riverFlow: {
        label: "River Flow (CFS)",
        yellow: { pctAboveNormal: 20,  explain: "Flow 20%+ above normal: Murky water" },
        orange: { pctAboveNormal: 50,  explain: "Flow 50%+ above normal: Unsafe wading" },
        red:    { pctAboveNormal: 100, explain: "Flow 100%+ above normal: Dangerous" },
      },
      lightning: {
        label: "Lightning / Thunderstorm",
        yellow: { keyword: "thunder", explain: "Thunderstorms in forecast" },
        orange: { keyword: "severe thunderstorm", explain: "Severe thunderstorm — get off the water" },
        red:    { keyword: "tornado", explain: "Tornado watch/warning" },
      },
      rain: {
        label: "Precipitation",
        yellow: { keyword: "rain", explain: "Rain in forecast: Turbid water, poor fishing" },
        orange: { keyword: "heavy rain", explain: "Heavy rain: Dangerous currents, flash flood risk" },
        red:    { keyword: "flash flood", explain: "Flash flood warning — evacuate immediately" },
      },
      snow: {
        label: "Snow",
        yellow: { keyword: "snow", explain: "Snow in forecast: Cold conditions, icy banks" },
        orange: { keyword: "heavy snow", explain: "Heavy snow: Dangerous footing, hypothermia risk" },
        red:    { keyword: "blizzard", explain: "Blizzard conditions: Do not fish" },
      },
      wind: {
        label: "Wind Speed",
        unit: "mph",
        yellow: { min: 20, explain: "Wind > 20 mph: Difficult casting" },
        orange: { min: 35, explain: "Wind > 35 mph: Unsafe on open water" },
        red:    { min: 50, explain: "Wind > 50 mph: Dangerous" },
      },
      nwsAlert: {
        label: "NWS Weather Alert",
        yellow: { severity: "Minor", explain: "NWS minor advisory in area" },
        orange: { severity: "Moderate", explain: "NWS moderate alert / watch" },
        red:    { severity: "Severe", explain: "NWS severe warning active" },
      },
    },
  },
  mountainBiking: {
    label: "Mountain Biking",
    factors: {
      tempHigh: {
        label: "High Temperature",
        unit: "°F",
        yellow: { min: 95, explain: "Temp > 95°F: Heat exhaustion risk during exertion" },
        orange: { min: 105, explain: "Temp > 105°F: Heat stroke danger" },
        red:    { min: 115, explain: "Temp > 115°F: Extreme heat emergency" },
      },
      tempLow: {
        label: "Low Temperature",
        unit: "°F",
        yellow: { max: 25, explain: "Temp < 25°F: Hypothermia risk" },
        orange: { max: 10, explain: "Temp < 10°F: Frostbite risk" },
        red:    { max: -10, explain: "Temp < -10°F: Extreme cold danger" },
      },
      aqi: {
        label: "Air Quality (AQI)",
        yellow: { min: 101, explain: "AQI 101–150: Reduced cardio performance" },
        orange: { min: 151, explain: "AQI 151–200: Unhealthy" },
        red:    { min: 201, explain: "AQI > 200: Avoid exertion outdoors" },
      },
      wind: {
        label: "Wind Speed",
        unit: "mph",
        yellow: { min: 25, explain: "Wind > 25 mph: Affects control" },
        orange: { min: 40, explain: "Wind > 40 mph: Dangerous on single-track" },
        red:    { min: 55, explain: "Wind > 55 mph: Do not ride" },
      },
      rain: {
        label: "Precipitation",
        yellow: { keyword: "rain", explain: "Rain in forecast: Muddy trails" },
        orange: { keyword: "heavy rain", explain: "Heavy rain: Trail damage likely" },
        red:    { keyword: "flash flood", explain: "Flash flood risk — stay clear" },
      },
      snow: {
        label: "Snow",
        yellow: { keyword: "snow", explain: "Snow in forecast: Slippery trails, reduced traction" },
        orange: { keyword: "heavy snow", explain: "Heavy snow: Trail closures likely, dangerous conditions" },
        red:    { keyword: "blizzard", explain: "Blizzard conditions: Do not ride" },
      },
      lightning: {
        label: "Lightning / Thunderstorm",
        yellow: { keyword: "thunder", explain: "Thunderstorms in forecast" },
        orange: { keyword: "severe thunderstorm", explain: "Severe thunderstorm" },
        red:    { keyword: "tornado", explain: "Tornado watch/warning" },
      },
      nwsAlert: {
        label: "NWS Weather Alert",
        yellow: { severity: "Minor", explain: "NWS minor advisory in area" },
        orange: { severity: "Moderate", explain: "NWS moderate alert / watch" },
        red:    { severity: "Severe", explain: "NWS severe warning active" },
      },
    },
  },
  rockClimbing: {
    label: "Rock Climbing",
    factors: {
      wind: {
        label: "Wind Speed",
        unit: "mph",
        yellow: { min: 20, explain: "Wind > 20 mph: Difficult at elevation" },
        orange: { min: 35, explain: "Wind > 35 mph: Dangerous on face" },
        red:    { min: 50, explain: "Wind > 50 mph: Do not climb" },
      },
      lightning: {
        label: "Lightning / Thunderstorm",
        yellow: { keyword: "thunder", explain: "Thunderstorms in forecast: Plan retreat" },
        orange: { keyword: "lightning", explain: "Lightning nearby — descend immediately" },
        red:    { keyword: "severe thunderstorm", explain: "Severe thunderstorm: Lethal exposure on rock" },
      },
      rain: {
        label: "Precipitation",
        yellow: { keyword: "rain", explain: "Rain in forecast: Slippery rock, reduced grip" },
        orange: { keyword: "heavy rain", explain: "Heavy rain: Dangerous holds, loose rock hazard" },
        red:    { keyword: "flash flood", explain: "Flash flood risk in canyons — do not climb" },
      },
      snow: {
        label: "Snow",
        yellow: { keyword: "snow", explain: "Snow in forecast: Icy rock, reduced grip" },
        orange: { keyword: "heavy snow", explain: "Heavy snow: Dangerous conditions, avalanche risk" },
        red:    { keyword: "blizzard", explain: "Blizzard conditions: Do not climb" },
      },
      tempHigh: {
        label: "High Temperature",
        unit: "°F",
        yellow: { min: 95, explain: "Temp > 95°F: Grip and stamina impacted" },
        orange: { min: 105, explain: "Temp > 105°F: Heat stroke risk" },
        red:    { min: 115, explain: "Temp > 115°F: Extreme danger" },
      },
      tempLow: {
        label: "Low Temperature",
        unit: "°F",
        yellow: { max: 30, explain: "Temp < 30°F: Numb fingers reduce grip" },
        orange: { max: 10, explain: "Temp < 10°F: Hypothermia risk at elevation" },
        red:    { max: -10, explain: "Temp < -10°F: Extreme cold danger" },
      },
      nwsAlert: {
        label: "NWS Weather Alert",
        yellow: { severity: "Minor", explain: "NWS minor advisory in area" },
        orange: { severity: "Moderate", explain: "NWS moderate alert / watch" },
        red:    { severity: "Severe", explain: "NWS severe warning active" },
      },
    },
  },
};

// Ordered risk levels for comparison
export const RISK_LEVELS = ["green", "yellow", "orange", "red"];

export const riskLevelMeta = {
  green:  { label: "Good to Go",    color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0", emoji: "✅" },
  yellow: { label: "Use Caution",   color: "#eab308", bg: "#fefce8", border: "#fde68a", emoji: "⚠️" },
  orange: { label: "Advisory",      color: "#f97316", bg: "#fff7ed", border: "#fed7aa", emoji: "🟠" },
  red:    { label: "Dangerous",     color: "#ef4444", bg: "#fef2f2", border: "#fecaca", emoji: "🔴" },
};

export default activityThresholds;
